import React, { useState, useEffect } from 'react';
import {
  Building2, MapPin, Phone, Mail, FileText, Edit2, Save, X, UserPlus,
  Loader2, CheckCircle, AlertCircle, Copy, Check, Upload, Trash2, Plus
} from 'lucide-react';
import { DirectoryPartner } from '../../types';
import {
  getDirectoryPartners,
  updateDirectoryPartner,
  addDirectoryPartner,
  deleteDirectoryPartner,
  markPartnerAccountCreated,
  importPartnersFromJson,
  ImportPartner
} from '../../services/directoryService';
import { createRetailerDirect } from '../../services/retailerService';
import { createRetailerAccount } from '../../services/accountService';
import { auth } from '../../firebase';

interface RetailerDirectoryTabProps {
  adminEmail: string;
}

const RetailerDirectoryTab: React.FC<RetailerDirectoryTabProps> = ({ adminEmail }) => {
  const [partners, setPartners] = useState<DirectoryPartner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<DirectoryPartner>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Account creation state
  const [creatingAccountFor, setCreatingAccountFor] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<{ email: string; password: string; partnerName: string } | null>(null);
  const [copiedField, setCopiedField] = useState<'email' | 'password' | null>(null);

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  // Add new partner state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPartner, setNewPartner] = useState({
    name: '',
    city: '',
    province: '',
    state: '',
    country: 'Canada',
    address: ''
  });

  useEffect(() => {
    loadPartners();
  }, []);

  // Escape close for modals
  useEffect(() => {
    if (!showImportModal && !showAddModal && !credentials) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (credentials) setCredentials(null);
      else if (showImportModal && !isImporting) setShowImportModal(false);
      else if (showAddModal && !isSaving) setShowAddModal(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showImportModal, showAddModal, credentials, isImporting, isSaving]);

  const loadPartners = async () => {
    setIsLoading(true);
    try {
      const data = await getDirectoryPartners();
      setPartners(data);
    } catch (err) {
      console.error('Error loading partners:', err);
      setError('Failed to load directory');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (partner: DirectoryPartner) => {
    setEditingId(partner.id);
    setEditForm({ ...partner });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSave = async () => {
    if (!editingId) return;
    setIsSaving(true);
    setError('');

    try {
      await updateDirectoryPartner(editingId, editForm);
      setPartners(prev =>
        prev.map(p => (p.id === editingId ? { ...p, ...editForm } : p))
      );
      setEditingId(null);
      setEditForm({});
      showSuccess('Partner information saved');
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}" from directory? This cannot be undone.`)) return;

    try {
      await deleteDirectoryPartner(id);
      setPartners(prev => prev.filter(p => p.id !== id));
      showSuccess('Partner removed from directory');
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const handleCreateAccount = async (partner: DirectoryPartner) => {
    // Validate required fields
    if (!partner.email) {
      setError(`Please add an email address for "${partner.name}" before creating an account.`);
      handleEdit(partner);
      return;
    }
    if (!partner.taxId) {
      setError(`Please add a Tax ID/HST for "${partner.name}" before creating an account.`);
      handleEdit(partner);
      return;
    }

    setCreatingAccountFor(partner.id);
    setError('');

    try {
      // Verify admin authentication
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Session expired. Please refresh and log in again.');
      }
      await currentUser.getIdToken(true);

      // Create Firebase Auth account
      const accountResult = await createRetailerAccount(
        partner.email,
        partner.name
      );

      // Create retailer record in Firestore
      const retailerId = await createRetailerDirect({
        businessName: partner.name,
        contactName: partner.contactName || partner.name,
        email: partner.email,
        phone: partner.phone || '',
        address: {
          street: partner.address || '',
          city: partner.city,
          province: partner.province || partner.state || '',
          postalCode: '',
          country: partner.country
        },
        taxId: partner.taxId
      }, adminEmail);

      // Update directory entry
      await markPartnerAccountCreated(partner.id, retailerId);

      // Update local state
      setPartners(prev =>
        prev.map(p => (p.id === partner.id ? {
          ...p,
          hasAccount: true,
          retailerId,
          accountCreatedAt: new Date()
        } : p))
      );

      // Show credentials
      setCredentials({
        email: accountResult.email,
        password: accountResult.tempPassword,
        partnerName: partner.name
      });

    } catch (err: any) {
      console.error('Account creation error:', err);
      if (err.code === 'functions/already-exists') {
        setError(`An account with email "${partner.email}" already exists.`);
      } else {
        setError(err.message || 'Failed to create account');
      }
    } finally {
      setCreatingAccountFor(null);
    }
  };

  const handleImport = async () => {
    setIsImporting(true);
    setError('');

    try {
      const data = JSON.parse(importJson) as ImportPartner[];
      if (!Array.isArray(data)) {
        throw new Error('JSON must be an array of partners');
      }

      const count = await importPartnersFromJson(data);
      showSuccess(`Imported ${count} partners successfully`);
      setShowImportModal(false);
      setImportJson('');
      loadPartners();
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    } finally {
      setIsImporting(false);
    }
  };

  const handleAddPartner = async () => {
    if (!newPartner.name || !newPartner.city || !newPartner.country) {
      setError('Name, City, and Country are required');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await addDirectoryPartner({
        name: newPartner.name,
        city: newPartner.city,
        province: newPartner.province || undefined,
        state: newPartner.state || undefined,
        country: newPartner.country,
        address: newPartner.address || undefined
      });
      showSuccess('Partner added to directory');
      setShowAddModal(false);
      setNewPartner({ name: '', city: '', province: '', state: '', country: 'Canada', address: '' });
      loadPartners();
    } catch (err: any) {
      setError(err.message || 'Failed to add partner');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = async (text: string, field: 'email' | 'password') => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Stats
  const totalPartners = partners.length;
  const withAccounts = partners.filter(p => p.hasAccount).length;
  const withoutAccounts = totalPartners - withAccounts;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-ember" strokeWidth={1.5} />
      </div>
    );
  }

  const inputClassSmall =
    'w-full px-3 py-2 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150';

  const inputClass =
    'w-full px-4 py-3 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-3">— Directory</p>
          <h2 className="font-editorial italic font-light text-3xl text-ink-950 tracking-tight">
            Retailer directory.
          </h2>
          <p className="text-ink-700 text-sm mt-1">Manage your B2B retail partners.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-ink-200 hover:border-ink-300 hover:bg-paper-bright text-ink-700 text-sm font-medium transition-colors duration-150"
          >
            <Upload className="w-4 h-4" strokeWidth={1.5} />
            Import JSON
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            Add partner
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-paper-bright p-5 border border-ink-200">
          <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Total partners</p>
          <p className="font-editorial italic text-2xl text-ink-950">{totalPartners}</p>
        </div>
        <div className="bg-paper-bright p-5 border border-ink-200">
          <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">With accounts</p>
          <p className="font-editorial italic text-2xl text-olive">{withAccounts}</p>
        </div>
        <div className="bg-paper-bright p-5 border border-ink-200">
          <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Need accounts</p>
          <p className="font-editorial italic text-2xl text-ember">{withoutAccounts}</p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-ember-soft/25 border-l-2 border-ember p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-ember-deep text-sm">{error}</p>
            <button onClick={() => setError('')} className="text-ember text-sm underline mt-1 hover:text-ember-deep transition-colors duration-150">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-olive/15 border-l-2 border-olive p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-olive" strokeWidth={1.5} />
          <p className="text-olive text-sm">{successMessage}</p>
        </div>
      )}

      {/* Partner Cards */}
      <div className="grid gap-4">
        {partners.map(partner => (
          <div
            key={partner.id}
            className={`bg-paper-bright border ${partner.hasAccount ? 'border-olive/40' : 'border-ink-200'} p-5`}
          >
            {editingId === partner.id ? (
              // Edit Mode
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1.5">Business name</label>
                    <input
                      type="text"
                      value={editForm.name || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      className={inputClassSmall}
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1.5">City</label>
                    <input
                      type="text"
                      value={editForm.city || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                      className={inputClassSmall}
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1.5">
                      {editForm.country === 'USA' ? 'State' : 'Province'}
                    </label>
                    <input
                      type="text"
                      value={editForm.country === 'USA' ? (editForm.state || '') : (editForm.province || '')}
                      onChange={e => setEditForm(prev => ({
                        ...prev,
                        [prev.country === 'USA' ? 'state' : 'province']: e.target.value
                      }))}
                      className={inputClassSmall}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1.5">Street address</label>
                    <input
                      type="text"
                      value={editForm.address || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                      className={inputClassSmall}
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1.5">Contact name</label>
                    <input
                      type="text"
                      value={editForm.contactName || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, contactName: e.target.value }))}
                      className={inputClassSmall}
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1.5">Phone</label>
                    <input
                      type="tel"
                      value={editForm.phone || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                      className={inputClassSmall}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1.5">
                      <Mail className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
                      Email (required for login)
                    </label>
                    <input
                      type="email"
                      value={editForm.email || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                      className={inputClassSmall}
                      placeholder="customer@example.com"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1.5">
                      <FileText className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
                      HST / Tax ID (required for account)
                    </label>
                    <input
                      type="text"
                      value={editForm.taxId || ''}
                      onChange={e => setEditForm(prev => ({ ...prev, taxId: e.target.value }))}
                      className={`${inputClassSmall} font-mono`}
                      placeholder="123456789RT0001"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1.5">Notes</label>
                  <textarea
                    value={editForm.notes || ''}
                    onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                    className={inputClassSmall}
                    rows={2}
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-ink-200 hover:border-ink-300 hover:bg-paper text-ink-700 text-sm font-medium transition-colors duration-150"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial flex items-center gap-2"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
                    Save changes
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <Building2 className="w-5 h-5 text-ink-500" strokeWidth={1.5} />
                    <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight">{partner.name}</h3>
                    {partner.hasAccount && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase bg-olive/15 text-olive border border-olive/40">
                        <CheckCircle className="w-3 h-3" strokeWidth={1.5} />
                        Has account
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-ink-700">
                      <MapPin className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                      <span>
                        {partner.city}, {partner.province || partner.state || ''} {partner.country}
                      </span>
                    </div>
                    {partner.address && (
                      <div className="text-ink-500">{partner.address}</div>
                    )}
                    {partner.email && (
                      <div className="flex items-center gap-2 text-ink-700">
                        <Mail className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                        <span>{partner.email}</span>
                      </div>
                    )}
                    {partner.phone && (
                      <div className="flex items-center gap-2 text-ink-700">
                        <Phone className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                        <span>{partner.phone}</span>
                      </div>
                    )}
                    {partner.taxId && (
                      <div className="flex items-center gap-2 text-ink-500">
                        <FileText className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                        <span className="font-mono text-xs">{partner.taxId}</span>
                      </div>
                    )}
                  </div>

                  {partner.notes && (
                    <p className="text-sm text-ink-500 mt-2 italic">{partner.notes}</p>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(partner)}
                    className="p-2 text-ink-500 hover:text-ink-700 hover:bg-paper transition-colors duration-150"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => handleDelete(partner.id, partner.name)}
                    className="p-2 text-ink-500 hover:text-ember hover:bg-ember-soft/25 transition-colors duration-150"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  {!partner.hasAccount && (
                    <button
                      onClick={() => handleCreateAccount(partner)}
                      disabled={creatingAccountFor === partner.id}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-olive hover:bg-olive/90 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial disabled:opacity-50"
                      title="Create account"
                    >
                      {creatingAccountFor === partner.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <UserPlus className="w-4 h-4" strokeWidth={1.5} />
                      )}
                      <span className="hidden sm:inline">Create account</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {partners.length === 0 && (
          <div className="text-center py-12 text-ink-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" strokeWidth={1.5} />
            <p className="text-sm">No partners in directory yet.</p>
            <p className="text-xs mt-1">Import your customer list or add partners manually.</p>
          </div>
        )}
      </div>

      {/* Credentials Modal */}
      {credentials && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm" onClick={() => setCredentials(null)} />
          <div className="relative bg-paper-bright shadow-float w-full max-w-md overflow-hidden">
            <div className="bg-forge text-paper px-6 py-5 relative">
              <button
                onClick={() => setCredentials(null)}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-olive flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-paper-warm" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-1">— Success</p>
                  <h2 className="font-editorial italic font-light text-2xl text-paper-warm tracking-tight">
                    Account created.
                  </h2>
                  <p className="text-paper/70 text-xs mt-1">{credentials.partnerName}</p>
                </div>
              </div>
            </div>

            <div className="p-6 lg:p-8">
              <p className="text-ink-700 text-sm mb-4">Share these login credentials with the customer:</p>

              <div className="space-y-4">
                <div>
                  <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                    Email
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-paper border border-ink-200 px-3 py-2 font-mono text-sm text-ink-900">
                      {credentials.email}
                    </code>
                    <button
                      onClick={() => copyToClipboard(credentials.email, 'email')}
                      className="p-2 border border-ink-200 hover:border-ink-300 hover:bg-paper transition-colors duration-150"
                    >
                      {copiedField === 'email' ? (
                        <Check className="w-4 h-4 text-olive" strokeWidth={1.5} />
                      ) : (
                        <Copy className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                    Temporary password
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-paper border border-ink-200 px-3 py-2 font-mono text-sm text-ink-900">
                      {credentials.password}
                    </code>
                    <button
                      onClick={() => copyToClipboard(credentials.password, 'password')}
                      className="p-2 border border-ink-200 hover:border-ink-300 hover:bg-paper transition-colors duration-150"
                    >
                      {copiedField === 'password' ? (
                        <Check className="w-4 h-4 text-olive" strokeWidth={1.5} />
                      ) : (
                        <Copy className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-sm text-ink-500 mt-4">
                Remind the customer to change their password after first login.
              </p>

              <button
                onClick={() => setCredentials(null)}
                className="w-full mt-6 px-4 py-3 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm" onClick={() => !isImporting && setShowImportModal(false)} />
          <div className="relative bg-paper-bright shadow-float w-full max-w-2xl overflow-hidden">
            <div className="bg-forge text-paper px-6 py-5 relative">
              <button
                onClick={() => setShowImportModal(false)}
                disabled={isImporting}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-2">— Import</p>
              <h2 className="font-editorial italic font-light text-2xl text-paper-warm tracking-tight">
                Import partners from JSON.
              </h2>
            </div>

            <div className="p-6 lg:p-8">
              <p className="text-ink-700 text-sm mb-4">
                Paste your JSON array below. Each object should have: name, city, province/state, country.
                Optional: address.
              </p>

              <textarea
                value={importJson}
                onChange={e => setImportJson(e.target.value)}
                className="w-full h-64 px-4 py-3 bg-paper border border-ink-200 font-mono text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150"
                placeholder='[
  { "name": "Store Name", "city": "Toronto", "province": "ON", "country": "Canada" },
  ...
]'
              />

              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => setShowImportModal(false)}
                  disabled={isImporting}
                  className="flex-1 px-4 py-2.5 border border-ink-200 hover:border-ink-300 hover:bg-paper text-ink-700 text-sm font-medium transition-colors duration-150"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={isImporting || !importJson.trim()}
                  className="flex-1 px-4 py-2.5 bg-ember hover:bg-ember-deep disabled:opacity-50 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial flex items-center justify-center gap-2"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" strokeWidth={1.5} />
                      Import
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Partner Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm" onClick={() => !isSaving && setShowAddModal(false)} />
          <div className="relative bg-paper-bright shadow-float w-full max-w-md overflow-hidden">
            <div className="bg-forge text-paper px-6 py-5 relative">
              <button
                onClick={() => setShowAddModal(false)}
                disabled={isSaving}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-2">— New partner</p>
              <h2 className="font-editorial italic font-light text-2xl text-paper-warm tracking-tight">
                Add partner.
              </h2>
            </div>

            <div className="p-6 lg:p-8 space-y-5">
              <div>
                <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Business name *</label>
                <input
                  type="text"
                  value={newPartner.name}
                  onChange={e => setNewPartner(prev => ({ ...prev, name: e.target.value }))}
                  className={inputClass}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">City *</label>
                  <input
                    type="text"
                    value={newPartner.city}
                    onChange={e => setNewPartner(prev => ({ ...prev, city: e.target.value }))}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                    {newPartner.country === 'USA' ? 'State' : 'Province'}
                  </label>
                  <input
                    type="text"
                    value={newPartner.country === 'USA' ? newPartner.state : newPartner.province}
                    onChange={e => setNewPartner(prev => ({
                      ...prev,
                      [prev.country === 'USA' ? 'state' : 'province']: e.target.value
                    }))}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Country *</label>
                <select
                  value={newPartner.country}
                  onChange={e => setNewPartner(prev => ({ ...prev, country: e.target.value }))}
                  className={`${inputClass} bg-paper-bright`}
                >
                  <option value="Canada">Canada</option>
                  <option value="USA">USA</option>
                </select>
              </div>

              <div>
                <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Street address</label>
                <input
                  type="text"
                  value={newPartner.address}
                  onChange={e => setNewPartner(prev => ({ ...prev, address: e.target.value }))}
                  className={inputClass}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2.5 border border-ink-200 hover:border-ink-300 hover:bg-paper text-ink-700 text-sm font-medium transition-colors duration-150"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddPartner}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2.5 bg-ember hover:bg-ember-deep disabled:opacity-50 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                  )}
                  Add partner
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailerDirectoryTab;
